import { Box, Button, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "../../../../../../components/CustomSelect";
import Flex from "../../../../../../components/Flex";
import Input from "../../../../../../components/Input";
import ReadOnlyFields from "../../../../../../components/ReadOnlyFields";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useVisitNoteTemplateCreate } from "../../../../../../queries/practice-setting/template/use-visit-note-template-create";
import { useVisitNoteTemplateUpdate } from "../../../../../../queries/practice-setting/template/use-visit-note-template-update";
import {
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../utils/utils";
import { PrimarySoapNoteForm } from "./sample-notes/PrimarySoapNoteForm";
import { PsychiatricNoteForm } from "./sample-notes/PsychiatricNoteForm";
import { SimpleSoapNoteForm } from "./sample-notes/SimpleSoapNoteForm";

export interface VisitNoteFormValues {
  templateName: string;
  templateType: string;
  followUp: string;
  instruction: string;
  hpi: string;
  problems: string;
  habits: string;
  carePlan: string;
  assessmentFreeNote: string;
  planFreeNote: string;
  subjectiveFreeNote: string;
  objectiveFreeNote: string;
  allergyFreeText: string;
}

interface VisitNoteTemplateFormProps {
  prefillData?: VisitNoteFormValues;
  readOnly: boolean;
  onSave?: () => void;
}

const VisitNoteTemplateForm = ({
  prefillData,
  readOnly,
  onSave,
}: VisitNoteTemplateFormProps) => {
  const { mutateAsync: createVisitNoteTemplate } = useVisitNoteTemplateCreate();
  const { mutateAsync: updateVisitNoteTemplate } = useVisitNoteTemplateUpdate();
  const showSnackbar = useSnackbar();

  const handleVisitNoteFormCreate = () => {
    const payload = {
      visitNoteTemplate: formik.values as any,
      xTENANTID: getXtenantId(),
    };
    showToastOnApiCall({
      apiCall: prefillData
        ? updateVisitNoteTemplate(payload)
        : createVisitNoteTemplate(payload),

      showSnackbar,
      onSave,
    });
  };

  const formik = useFormik<VisitNoteFormValues>({
    initialValues: prefillData
      ? { ...visitNoteinitialValues, ...prefillData }
      : visitNoteinitialValues,
    validationSchema: Yup.object().shape({
      templateName: Yup.string().required("Template name is required"),
      templateType: Yup.string().required("Visit note type is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleVisitNoteFormCreate,
  });

  const formMapping: Record<string, React.ReactNode> = {
    SIMPLE_SOAP_NOTE: (
      <SimpleSoapNoteForm formik={formik} readOnly={readOnly} />
    ),
    PSYCHIATRIC_NOTE: (
      <PsychiatricNoteForm formik={formik} readOnly={readOnly} />
    ),
    PRIMARY_CARE_SOAP_NOTE: (
      <PrimarySoapNoteForm formik={formik} readOnly={readOnly} />
    ),
  };

  return (
    <ReadOnlyFields active={readOnly}>
      <Flex flexDirection="column" gap={2}>
        <Flex flexDirection={"column"} gap={2}>
          {/* <CustomTitle>Visit Note Template</CustomTitle> */}

          <BasicFields formik={formik} readonly={readOnly} />
          <Box sx={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
            {formMapping[formik.values.templateType]}
          </Box>

          {!readOnly && (
            <Flex justifyContent={"end"}>
              <Button variant="contained" onClick={formik.submitForm}>
                Save
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </ReadOnlyFields>
  );
};

type BasicFieldsProps = {
  formik: FormikProps<VisitNoteFormValues>;
  readonly: boolean;
};

const BasicFields = ({ formik, readonly }: BasicFieldsProps) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <CustomSelect
          readOnly={readonly}
          required
          label="Visit Note Type"
          // options={DEFAULT_FORM_LIST?.map((it) => ({
          //   label: it.name,
          //   value: it.value,
          // }))}
          options={visitNoteTypeOptions}
          value={formik.values.templateType}
          onChange={(value) => formik.setFieldValue("templateType", value)}
        />
      </Grid>
      <Grid item xs={6}>
        <Input
          required
          label="Name Of Template"
          textFieldProps={{
            disabled: readonly,
            name: "templateName",
            value: formik.values.templateName,
            onChange: formik.handleChange,
            placeholder: "Enter Template Name",
          }}
          error={formik.touched?.templateName && formik.errors?.templateName}
        />
      </Grid>
    </Grid>
  );
};

export default VisitNoteTemplateForm;

export const visitNoteTypeOptions: any[] = [
  { label: "Simple SOAP Note", value: "SIMPLE_SOAP_NOTE" },
  { label: "Psychiatric Note", value: "PSYCHIATRIC_NOTE" },
  { label: "Primary Care SOAP Note", value: "PRIMARY_CARE_SOAP_NOTE" },
];

export const visitNoteinitialValues: VisitNoteFormValues = {
  templateName: "",
  templateType: "SIMPLE_SOAP_NOTE",
  followUp: "",
  instruction: "",
  hpi: "",
  problems: "",
  habits: "",
  carePlan: "",
  assessmentFreeNote: "",
  planFreeNote: "",
  subjectiveFreeNote: "",
  objectiveFreeNote: "",
  allergyFreeText: "",
};
